import { forwardRef, useMemo } from "react";
import { type Column } from "@racoon/ui";
import { useT, Namespace } from "@racoon/i18n";
import { UniversalReportRow, useUniversalReportRows } from "../model/use-universal-report-rows";
import { useReportHyperlinks } from "../model/use-report-hyperlinks";
import {
  amountCell,
  byNumber,
  compareIds,
  entityNumber,
  numberColumn,
  reportDate,
  sumOf,
  textCell,
} from "../model/job-report";
import { ReportTable, ReportTableHandle, ReportTableProps } from "./report-table";

/** A report row, or the totals line that closes the exports. */
type UniversalExportRow = UniversalReportRow & { isTotal?: boolean };

/** «Универсальный отчёт по вывозу» (AIS 15.1.3): collections per site, day, container type and crew. */
export const UniversalReportTable = forwardRef<ReportTableHandle, ReportTableProps>(
  (props, ref) => {
    const t = useT();
    const { rows, isLoading, error } = useUniversalReportRows(props.startDate, props.endDate);
    const { locationCell, customerCell, driverCell, plateCell, entityCards } =
      useReportHyperlinks();

    const columns: Column<UniversalExportRow>[] = useMemo(() => {
      return [
        numberColumn(
          t(Namespace.Analytics, "columns.number"),
          t(Namespace.Analytics, "collection.totals.label"),
        ),
        {
          id: "id",
          header: t(Namespace.Analytics, "columns.id"),
          // A row is a pickup location's collections of one container type on one day.
          accessor: (row) => (row.isTotal ? "" : entityNumber("site", row.pickupLocationId)),
          width: 100,
          sortable: true,
          sortFn: (a, b) => compareIds(a.pickupLocationId, b.pickupLocationId),
        },
        {
          id: "date",
          header: t(Namespace.Analytics, "table.date"),
          accessor: (row) => (row.isTotal ? "" : reportDate(row.date)),
          width: 120,
          sortable: true,
          sortFn: byNumber((row) => row.date?.getTime()),
        },
        {
          id: "client",
          header: t(Namespace.Analytics, "table.customer"),
          accessor: (row) => textCell(row.serviceReceiverName, row.isTotal),
          width: 200,
          sortable: true,
          cell: (info) =>
            customerCell(
              info.row.original.serviceReceiverId,
              info.row.original.serviceReceiverName,
            ),
        },
        {
          id: "bin",
          header: t(Namespace.Analytics, "columns.bin"),
          accessor: (row) => textCell(row.serviceReceiverBin, row.isTotal),
          width: 130,
          sortable: true,
        },
        {
          id: "site",
          header: t(Namespace.Analytics, "columns.site"),
          accessor: (row) => textCell(row.pickupLocationName, row.isTotal),
          width: 200,
          sortable: true,
          cell: (info) =>
            locationCell(info.row.original.pickupLocationId, info.row.original.pickupLocationName),
        },
        {
          id: "containerType",
          header: t(Namespace.Analytics, "collection.containerType"),
          accessor: (row) => textCell(row.containerTypeName, row.isTotal),
          width: 160,
          sortable: true,
        },
        {
          id: "count",
          header: t(Namespace.Analytics, "collection.count"),
          accessor: (row) => amountCell(row.numberOfContainers, 0, row.isTotal),
          width: 120,
          sortable: true,
          sortFn: byNumber((row) => row.numberOfContainers),
        },
        {
          id: "volume",
          header: t(Namespace.Analytics, "columns.nominalVolume"),
          accessor: (row) => amountCell(row.containerVolume, 2, row.isTotal),
          width: 130,
          sortable: true,
          sortFn: byNumber((row) => row.containerVolume),
        },
        {
          id: "factVolume",
          header: t(Namespace.Analytics, "columns.factVolume"),
          accessor: (row) => amountCell(row.factTotalVolume, 2, row.isTotal),
          width: 130,
          sortable: true,
          sortFn: byNumber((row) => row.factTotalVolume),
        },
        {
          id: "collections",
          header: t(Namespace.Analytics, "summary.collections"),
          accessor: (row) => row.collections,
          width: 110,
          sortable: true,
          sortFn: byNumber((row) => row.collections),
        },
        {
          id: "truck",
          header: t(Namespace.Analytics, "collection.truck"),
          accessor: (row) => textCell(row.truckPlateNumber, row.isTotal),
          width: 160,
          sortable: true,
          cell: (info) => plateCell(info.row.original.truckId, info.row.original.truckPlateNumber),
        },
        {
          id: "driver",
          header: t(Namespace.Analytics, "collection.driver"),
          accessor: (row) => textCell(row.driverName, row.isTotal),
          width: 220,
          sortable: true,
          cell: (info) => driverCell(info.row.original.driverId, info.row.original.driverName),
        },
      ];
    }, [t, locationCell, customerCell, driverCell, plateCell]);

    const exportRows = useMemo<UniversalExportRow[]>(
      () =>
        rows.length
          ? [
              ...rows,
              {
                isTotal: true,
                key: "total",
                date: null,
                pickupLocationId: 0n,
                serviceReceiverId: null,
                serviceReceiverName: "",
                serviceReceiverBin: "",
                pickupLocationName: "",
                containerTypeName: "",
                numberOfContainers: sumOf(rows.map((r) => r.numberOfContainers)),
                containerVolume: sumOf(rows.map((r) => r.containerVolume)),
                factTotalVolume: sumOf(rows.map((r) => r.factTotalVolume)),
                collections: rows.reduce((sum, r) => sum + r.collections, 0),
                truckId: null,
                truckPlateNumber: "",
                driverId: null,
                driverName: "",
              },
            ]
          : rows,
      [rows],
    );

    return (
      <ReportTable
        {...props}
        exportRef={ref}
        settingsKey="universal_report"
        reportName={t(Namespace.Analytics, "reports.summary")}
        columns={columns}
        rows={rows}
        exportRows={exportRows}
        rowKey={(row) => row.key}
        isLoading={isLoading}
        error={error}
      >
        {entityCards}
      </ReportTable>
    );
  },
);
